import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { kycApiService } from '../services/kycApiService';
import {
  type UserIdentityCard,
  type UserKyc,
  KycStatus,
  type UserKycState,
  KycAddressStatus,
  type UpdateAddressRequest,
  type UploadProofOfAddressRequest,
} from '../types/kyc';

const ONE_HOUR = 1000 * 60 * 60;

export const KycQueryKeys = {
  kyc: 'kyc',
  userIdentityCard: 'user-identity-card',
  kycState: 'kyc-state',
};

interface UseKycReturn {
  isSessionRequesting: boolean;
  isAddressUpdating: boolean;
  isProofUploading: boolean;
  isKycLoading: boolean;
  isKycInProgress: boolean;
  isKycInvalid: boolean;
  isKycFailed: boolean;
  isUserActionRequired: boolean;
  isKycValid: boolean;
  needsManualAddress: boolean;
  needsProofOfAddress: boolean;
  requiresProofOfAddressVerification: boolean;
  isIdentityCardLoading: boolean;
  kyc: UserKyc | undefined;
  identityCard: UserIdentityCard | undefined;
  kycState: UserKycState | undefined;
  getKyc: () => Promise<UserKyc | undefined>;
  getKycState: () => Promise<UserKycState | undefined>;
  getUserIdentityCard: () => Promise<UserIdentityCard | undefined>;
  requestKycSession: (isExpired?: boolean) => Promise<UserKyc>;
  updateAddress: (request: UpdateAddressRequest) => Promise<UserKycState>;
  uploadProofOfAddress: (request: UploadProofOfAddressRequest) => Promise<UserKycState>;
}

export const useKyc = (fetchKyc: boolean = false): UseKycReturn => {
  const queryClient = useQueryClient();

  const kycQuery = useQuery({
    queryKey: [KycQueryKeys.kyc],
    queryFn: () => kycApiService.getUserKyc(),
    staleTime: ONE_HOUR,
  });

  const kycStateQuery = useQuery({
    queryKey: [KycQueryKeys.kycState],
    queryFn: () => kycApiService.getUserKycState(),
    enabled: fetchKyc,
    staleTime: ONE_HOUR,
  });

  const userIdentityCardQuery = useQuery({
    queryKey: [KycQueryKeys.userIdentityCard],
    queryFn: () => kycApiService.getUserIdentityCard(),
  });

  const requestKycSessionMutation = useMutation({
    mutationFn: (isExpired?: boolean) =>
      kycApiService.requestKycSession(isExpired),
  });

  const updateAddressMutation = useMutation({
    mutationFn: (request: UpdateAddressRequest) =>
      kycApiService.updateAddress(request),
    onSuccess: (kycState) => {
      queryClient.setQueryData([KycQueryKeys.kycState], kycState);
    },
  });

  const uploadProofOfAddressMutation = useMutation<
    UserKycState,
    Error,
    UploadProofOfAddressRequest
  >({
    mutationFn: ({ file, documentType }) =>
      kycApiService.uploadProofOfAddress(file, documentType),
    onSuccess: (kycState) => {
      queryClient.setQueryData([KycQueryKeys.kycState], kycState);
    },
  });

  const getKyc = async () => {
    const result = await kycQuery.refetch();
    return result.data;
  };

  const getKycState = async () => {
    const result = await kycStateQuery.refetch();
    return result.data;
  };

  const getUserIdentityCard = async () => {
    const result = await userIdentityCardQuery.refetch();
    return result.data;
  };

  const isKycInProgress =
    kycStateQuery.data?.status === KycStatus.Submitted ||
    kycStateQuery.data?.status === KycStatus.ReviewRequired ||
    kycStateQuery.data?.addressStatus === KycAddressStatus.InProgress;

  const isUserActionRequired =
    kycStateQuery.data?.status === KycStatus.InProgress ||
    kycStateQuery.data?.status === KycStatus.NotStarted ||
    kycStateQuery.data?.status === KycStatus.Expired;

  const needsManualAddress =
    kycStateQuery.data?.status === KycStatus.Valid &&
    kycStateQuery.data?.isIdentityCardValid === true &&
    kycStateQuery.data?.requiresManualAddress === true &&
    kycStateQuery.data?.hasUsableAddress === false;

  const needsProofOfAddress =
    kycStateQuery.data?.status === KycStatus.Valid &&
    kycStateQuery.data?.isIdentityCardValid === true &&
    kycStateQuery.data?.hasUsableAddress === true &&
    kycStateQuery.data?.requiresProofOfAddress === true &&
    kycStateQuery.data?.isProofPendingVerification === false;

  const isKycValid =
    kycStateQuery.data?.status === KycStatus.Valid &&
    kycStateQuery.data?.isIdentityCardValid &&
    kycStateQuery.data?.hasUsableAddress;

  const isKycInvalid =
    kycStateQuery.data?.status === KycStatus.Invalid ||
      kycStateQuery.data?.isIdentityCardValid === false;

  // Rejected with nothing left for the user to do: only a manual validation
  // by support moves it on (KycGuard's failed page).
  const isKycFailed = isKycInvalid && !isKycInProgress && !isUserActionRequired;

  const requiresProofOfAddressVerification =
    (kycStateQuery.data?.isProofPendingVerification ?? false) ||
    (kycStateQuery.data?.requiresProofOfAddress ?? false);

  return {
    isSessionRequesting: requestKycSessionMutation.isPending,
    isAddressUpdating: updateAddressMutation.isPending,
    isProofUploading: uploadProofOfAddressMutation.isPending,
    isKycLoading: kycQuery.isLoading || kycStateQuery.isLoading,
    isKycInProgress,
    isKycInvalid,
    isKycFailed,
    isUserActionRequired,
    isKycValid,
    needsManualAddress,
    needsProofOfAddress,
    requiresProofOfAddressVerification,
    isIdentityCardLoading: userIdentityCardQuery.isLoading,
    kyc: kycQuery.data,
    identityCard: userIdentityCardQuery.data,
    kycState: kycStateQuery.data,
    getKyc,
    getKycState,
    getUserIdentityCard,
    requestKycSession: requestKycSessionMutation.mutateAsync,
    updateAddress: updateAddressMutation.mutateAsync,
    uploadProofOfAddress: uploadProofOfAddressMutation.mutateAsync,
  };
};
